import { VOICE_SPEEDS, getVoiceSpeed, playVoice, playVoiceWords, stopVoice, wordAtFraction } from "@/lib/tts";

// Recorded phonics sounds + Kiko's recorded child voice (served by the backend); the phone voice is only a fallback.
const CLIP = {
  en: { a: "a", b: "b", c: "c-k", k: "c-k", ck: "c-k", d: "d", e: "e", f: "f", g: "g", h: "h", i: "i", j: "j", l: "l", m: "m", n: "n", o: "o",
    p: "p", qu: "qu", kw: "qu", r: "r", s: "s", t: "t", u: "u", v: "v", w: "w", x: "x", ks: "x", y: "y", z: "z" },
  fr: { a: "a", b: "b", c: "c-k", k: "c-k", d: "d", e: "e", "é": "e_aigu", "è": "e_grave_chapeau", "ê": "e_grave_chapeau", f: "f", g: "g",
    i: "i", j: "j", l: "l", m: "m", n: "n", o: "o", p: "p", qu: "qu", r: "r", s: "s", t: "t", u: "u", v: "v", w: "w", x: "x", y: "y", z: "z" },
};
const clipUrl = (lang, tok) => {
  const t = String(tok).toLowerCase().replace(/[/\s]/g, "").replace(/\(.*$/, "");
  const key = (lang === "fr" && /^\/?y\/?\s*\(son u\)/i.test(tok) ? "u" : null) || CLIP[lang]?.[t];
  return key ? `/reading/sounds/${lang.toUpperCase()}_son_${key}.mp3?v=6` : null;
};

let current = null;
let run = 0;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const API = process.env.REACT_APP_BACKEND_URL;
const voiceUrl = (text, lang) => `${API}/api/reading/voice/${lang}?v=6&t=${encodeURIComponent(text)}`;

// Kiko's sentences play a little slower than recorded so young readers can follow; letter sounds keep their speed.
const READ_PACE = 0.9;
const rateOf = (pace = 1) => (VOICE_SPEEDS[getVoiceSpeed()] || 1) * pace;

function playUrl(url, pace = 1) {
  return new Promise((resolve) => {
    const a = new Audio(url);
    a.preservesPitch = true;
    a.playbackRate = rateOf(pace);
    current = a;
    a.onended = () => resolve(true);
    a.onpause = () => resolve(true);
    a.onerror = () => resolve(false);
    a.play().catch(() => resolve(false));
  });
}

// Kiko reads a line: "/s/" parts play the sound clip, the rest plays Kiko's recorded voice.
// "s - a - t" written in a sentence -> the three recorded sounds (not the letter names)
const SEQ = /(^|[\s:(])((?:[a-zéèêàù]{1,2} - )+[a-zéèêàù]{1,2})(?=[\s.,!?:)]|$)/gi;
export const withSounds = (text, lang) => String(text).replace(SEQ, (m, pre, seq) => pre + seq.split(" - ").map((u) => (clipUrl(lang, u) ? `/${u}/` : u)).join(" "));

async function kikoSay(text, lang) {
  const my = run;
  const parts = withSounds(text, lang).split(/(\/[^/\s]{1,6}\/)/).map((p) => p.trim()).filter(Boolean);
  for (const p of parts) {
    if (my !== run) return;
    const clip = /^\/.+\/$/.test(p) ? clipUrl(lang, p.slice(1, -1)) : null;
    if (clip) { await playUrl(clip); continue; } // eslint-disable-line no-await-in-loop
    const txt = p.replace(/\//g, "").trim();
    if (!/[\p{L}\p{N}]/u.test(txt)) continue;
    const ok = await playUrl(voiceUrl(txt, lang), READ_PACE); // eslint-disable-line no-await-in-loop
    if (!ok && my === run) await playVoice(txt, lang); // eslint-disable-line no-await-in-loop
  }
}

export function stopReading() {
  run += 1;
  if (current) { current.pause(); current = null; }
  stopVoice();
}

// "/s/" -> recorded sound; "c - a - t" -> each sound in turn; "avion, ananas" -> each word; else Kiko reads it.
// "c - a - t": one recorded clip with the sounds back to back (smooth), parts lit in turn; false if there is no clip
const blendUrl = (lang, seq) => `/reading/blends/${lang}/${seq.split(" - ").map((t) => t.trim().toLowerCase().replace(/[^\p{L}\p{N}_-]/gu, "")).join("_")}.mp3?v=2`;
export function playBlend(seq, lang, onPart) {
  stopReading();
  const n = seq.split(" - ").length;
  return new Promise((resolve) => {
    const a = new Audio(blendUrl(lang, seq));
    current = a;
    a.ontimeupdate = () => onPart && a.duration && onPart(Math.min(n - 1, Math.floor((a.currentTime / a.duration) * n)));
    a.onended = () => resolve(true);
    a.onpause = () => resolve(true);
    a.onerror = () => resolve(false);
    a.play().catch(() => resolve(false));
  });
}

export async function playReadingAudio(audio, lang) {
  if (!audio) return;
  stopReading();
  const txt = String(audio).trim();
  if (txt.includes(" - ") && (await playBlend(txt, lang))) return;
  const my = run;
  if (/^\/.+\//.test(txt) || /^\/y\/ \(son u\)$/.test(txt)) {
    const u = clipUrl(lang, txt.match(/^\/([^/]+)\//)?.[1] || txt);
    if (u) return playUrl(u);
    return kikoSay(txt.replace(/\//g, ""), lang);
  }
  if (txt.includes(" - ")) {
    for (const tok of txt.split(" - ")) {
      if (my !== run) return;
      const u = clipUrl(lang, tok.trim());
      if (u) await playUrl(u);
      else await kikoSay(tok.trim(), lang);
      await wait(350);
    }
    return;
  }
  if (txt.includes(",")) {
    for (const w of txt.split(",")) {
      if (my !== run) return;
      await kikoSay(w.trim(), lang);
      await wait(250);
    }
    return;
  }
  return kikoSay(txt, lang);
}

export const playSay = (text, lang) => { stopReading(); return kikoSay(text, lang); };

// Kiko reads a whole sentence in one smooth go while onWord(k) lights up the word being read.
export async function playSayHighlight(text, lang, onWord) {
  stopReading();
  const my = run;
  const ok = await new Promise((resolve) => {
    const a = new Audio(voiceUrl(String(text).trim(), lang));
    a.preservesPitch = true;
    a.playbackRate = rateOf(READ_PACE);
    current = a;
    let tick = null;
    const end = (v) => { clearInterval(tick); resolve(v); };
    a.onplaying = () => { clearInterval(tick); tick = setInterval(() => a.duration && onWord(wordAtFraction(text, a.currentTime / a.duration)), 80); };
    a.onended = () => end(true);
    a.onpause = () => end(true);
    a.onerror = () => end(false);
    a.play().catch(() => end(false));
  });
  if (!ok && my === run) await playVoiceWords(text, lang, (k) => my === run && onWord(k));
  if (my === run) onWord(-1);
}
export { clipUrl };
